import { CSSProperties, useEffect, useRef } from 'react';
import styles from './CompanyVision.module.scss';
import greetingMark from '../images/about/greeting-mark.png';
import bandBg from '../images/hero-bg2.jpg';

// 가치·플랫폼 카드 아이콘: public/images/about 에 두어 base64 인라인 없이 짧은 경로로 연결
const ICON_BASE = `${process.env.PUBLIC_URL}/images/about`;

const VALUES = [
  { img: `${ICON_BASE}/value-01.png`, title: 'Better Printing', desc: '작은 제작물 하나까지 완성도 높은 품질을 추구합니다.' },
  { img: `${ICON_BASE}/value-02.png`, title: 'Easy Process', desc: '상담부터 제작, 납품까지 체계적인 프로세스를 제공합니다.' },
  { img: `${ICON_BASE}/value-03.png`, title: 'Creative Connection', desc: '고객의 생각과 브랜드가 실제 인쇄물로 완성될 수 있도록 함께합니다.' },
  { img: `${ICON_BASE}/value-04.png`, title: 'Reliable Partner', desc: '신뢰할 수 있는 품질과 책임 있는 서비스로 고객과 함께 성장합니다.' },
];

const RAIL_STEPS = ['기획', '디자인', '인쇄', '후가공', '검수', '납품'];

// iconSize: 아이콘이 작아 보이는 카드만 지정 (기본 56px)
const PLATFORM: { num: string; en: string; ko: string; desc: string; icon: string; iconSize?: string }[] = [
  {
    num: '01.',
    en: 'Consulting',
    ko: '상담',
    desc: '제작 목적과 용도, 수량, 일정 등을 확인하고 가장 적합한 제작 방법과 사양을 제안합니다.',
    icon: `${ICON_BASE}/platform-01.png`,
  },
  {
    num: '02.',
    en: 'Design',
    ko: '디자인',
    desc: '고객의 아이디어와 브랜드 방향을 이해하고 목적에 맞는 디자인과 인쇄 데이터를 준비합니다.',
    icon: `${ICON_BASE}/platform-02.png`,
  },
  {
    num: '03.',
    en: 'Printing',
    ko: '인쇄',
    desc: '제작물의 특성과 수량에 맞는 인쇄 방식을 적용하여 안정적인 품질로 제작합니다.',
    icon: `${ICON_BASE}/platform-03.png`,
  },
  {
    num: '04.',
    en: 'Finishing',
    ko: '후가공',
    desc: '코팅, 접지, 제본 등 다양한 후가공을 통해 인쇄물의 완성도와 활용도를 높입니다.',
    icon: `${ICON_BASE}/platform-04.png`,
  },
  {
    num: '05.',
    en: 'Quality Check',
    ko: '검수',
    desc: '제작 완료 후 인쇄 상태와 사양을 확인하여 완성도 높은 결과물을 제공합니다.',
    icon: `${ICON_BASE}/platform-05.png`,
  },
  {
    num: '06.',
    en: 'Delivery',
    ko: '납품',
    desc: '최종 검수가 완료된 제작물을 고객이 원하는 일정과 장소에 안전하게 전달합니다.',
    icon: `${ICON_BASE}/platform-06.png`,
    iconSize: '68px',
  },
];

// 등장 지연시간(--d) 등 CSS 변수를 style로 넘기기 위한 헬퍼
const cssVars = (vars: Record<string, string>) => vars as CSSProperties;

/** 회사소개 > 비전과 회사플랫폼 (원본: gworks-about.html 의 HERO / VISION / PLATFORM) */
function CompanyVision() {
  const rootRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLOListElement>(null);

  // 스크롤 등장: .reveal 요소가 화면에 들어오면 .isIn 추가 (한 번만)
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const items = root.querySelectorAll<HTMLElement>(`.${styles.reveal}`);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add(styles.isIn));
      railRef.current?.classList.add(styles.railIn);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add(styles.isIn);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.14, rootMargin: '0px 0px -6% 0px' },
    );
    items.forEach((el) => io.observe(el));

    // 프로세스 라인: 충분히 보였을 때 선이 그려짐
    const rail = railRef.current;
    const ro = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && rail) {
            rail.classList.add(styles.railIn);
            ro.unobserve(rail);
          }
        });
      },
      { threshold: 0.4 },
    );
    if (rail) ro.observe(rail);

    return () => {
      io.disconnect();
      ro.disconnect();
    };
  }, []);

  return (
    <div ref={rootRef} className={styles.root}>
      {/* HERO / INTRO */}
      <section className={styles.hero}>
        <div className={styles.wrap}>
          <div className={styles.heroCore}>
            <img className={`${styles.heroMark} ${styles.reveal}`} src={greetingMark} alt="DESIGN&COMPANY G-WORKS" />
            <h2 className={`${styles.heroTitle} ${styles.reveal}`} style={cssVars({ '--d': '.06s' })}>
              생각을 현실로 만드는 인쇄,
              <br className={styles.lb} /> <span className={styles.brand}>G-WORKS</span>가 함께합니다.
            </h2>
            <p className={`${styles.lead} ${styles.reveal}`} style={cssVars({ '--d': '.12s' })}>
              <strong>G-WORKS</strong>는 단순히 인쇄물을 제작하는 것을 넘어
              <br className={styles.lb} />
              고객의 <strong>아이디어와 브랜드</strong>가 더 나은 모습으로 전달될 수 있도록
              <br className={styles.lb} />
              디자인부터 제작, 인쇄, 후가공까지 연결하는 <strong>통합 인쇄 서비스</strong>를 만들어갑니다.
            </p>
          </div>

          <div className={styles.values}>
            {VALUES.map((v, i) => (
              <article
                key={v.title}
                className={`${styles.value} ${styles.reveal}`}
                style={cssVars({ '--d': `${0.05 + i * 0.07}s` })}
              >
                <span className={styles.valueIcon}>
                  <img className={styles.valueImg} src={v.img} alt="" />
                </span>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* VISION */}
      <section className={styles.section}>
        <div className={styles.wrap}>
          <div className={styles.sectionHead}>
            <h2 className={`${styles.h2Vision} ${styles.reveal}`}><span className={styles.brand}>G-WORKS</span> VISION</h2>
            <div className={styles.visionBody}>
              <p className={styles.reveal} style={cssVars({ '--d': '.06s' })}>
                아이디어가 인쇄물이 되는 모든 순간, <strong>가장 믿을 수 있는 제작 파트너</strong>가 되는 것.
                <br className={styles.lb} />
                이것이 G-WORKS가 만들어가는 방향입니다.
              </p>
              {/* 배경 이미지는 --band-img 값으로 지정 (예: url(이미지경로)) — 없으면 어두운 회색 */}
              <p className={`${styles.visionBand} ${styles.reveal}`} style={cssVars({ '--d': '.12s', '--band-img': `url(${bandBg})` })}>
                디자인부터 제작까지, 하나의 플랫폼에서 연결합니다.
                <br className={styles.lb} />
                인쇄물을 만들기 위해 디자인 업체, 인쇄 업체, 후가공 업체를 각각 찾을 필요가 없도록.
                <br className={styles.lb} />
                G-WORKS는 고객의 아이디어가 하나의 완성된 결과물이 되기까지
                <br className={styles.lb} />
                필요한 모든 과정을 유기적으로 연결합니다.
              </p>
            </div>
          </div>

          <ol ref={railRef} className={`${styles.rail} ${styles.reveal}`} aria-label="G-WORKS 제작 프로세스 6단계">
            {RAIL_STEPS.map((step) => (
              <li key={step} className={styles.step}>
                <span className={styles.dot}>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* PLATFORM */}
      <section className={styles.section}>
        <div className={styles.wrap}>
          <div className={styles.sectionHead}>
            <h2 className={`${styles.h2Platform} ${styles.reveal}`}><span className={styles.brand}>G-WORKS</span> PRINTING PLATFORM</h2>
            <p className={`${styles.c2} ${styles.reveal}`} style={cssVars({ '--d': '.06s' })}>
              단순한 인쇄 제작사가 아닌 고객과 제작자를 연결하고
              <br className={styles.lb} />
              아이디어와 결과물을 연결하는 통합 인쇄 제작 플랫폼, G-WORKS입니다.
            </p>
          </div>

          <div className={styles.platformGrid}>
            {PLATFORM.map((p, i) => (
              <article
                key={p.num}
                className={`${styles.pcard} ${styles.reveal}`}
                style={cssVars({
                  '--d': `${0.04 + i * 0.04}s`,
                  ...(p.icon ? { '--icon': `url(${p.icon})` } : {}),
                  ...(p.iconSize ? { '--icon-size': p.iconSize } : {}),
                })}
              >
                <p className={styles.phead}>
                  <span className={styles.pnum}>{p.num}</span>
                  <span className={styles.pen}>{p.en}</span>
                  <span className={styles.pko}>{p.ko}</span>
                </p>
                <p className={styles.pbody}>{p.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default CompanyVision;
